import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import moment from 'moment'
import IconButton from '@material-ui/core/IconButton'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import CircularProgress from '@material-ui/core/CircularProgress'
import formatCurrency from '../../../utils/formatCurrency'

const Section = styled.section`
  margin-top: 28px;
`

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(5, 5, 25, 0.06);
  padding: 0;
  overflow: hidden;
`

const TblHead = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #e8e8e8;
  gap: 12px;
`

const HeadT = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #050519;
`

const HeadSub = styled.span`
  font-size: 12px;
  color: #676767;
  margin-left: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 18px;
    text-align: left;
    border-bottom: 1px solid #e8e8e8;
  }
  th {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #676767;
    background: #fafafa;
  }
  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`

const UtilBar = styled.div`
  position: relative;
  height: 6px;
  border-radius: 3px;
  background: #f0f0f0;
  overflow: hidden;
  min-width: 80px;
  flex: 1;
`

const Fill = styled.div`
  height: 100%;
  background: ${p => p.$c};
  border-radius: 3px;
  width: ${p => p.$w};
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
`

const MissingChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: #fbe7e5;
  color: #d0342c;
`

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  background: ${p => p.$bg};
`

const WeekGrid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin: 4px 0 8px 44px;
  th,
  td {
    padding: 6px 12px;
    border-bottom: 1px solid #f0f0f0;
  }
  th {
    font-size: 10px;
    font-weight: 600;
    color: #676767;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: transparent;
  }
  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`

const avColors = ['#4241BF', '#00A162', '#D98E00', '#D0342C']

const weekShape = PropTypes.shape({
  weekStart: PropTypes.string.isRequired,
  weekEnd: PropTypes.string.isRequired,
  hoursLogged: PropTypes.number.isRequired,
  assignedHours: PropTypes.number.isRequired,
  entriesCount: PropTypes.number.isRequired,
})

const rowShape = PropTypes.shape({
  personId: PropTypes.string.isRequired,
  personName: PropTypes.string.isRequired,
  hoursLogged: PropTypes.number.isRequired,
  entriesCount: PropTypes.number.isRequired,
  weeklyAssignedHours: PropTypes.number,
  utilizationPct: PropTypes.number,
  fees: PropTypes.number.isRequired,
  weeklyBreakdown: PropTypes.arrayOf(weekShape),
})

function formatWeekRange(weekStart, weekEnd) {
  const start = moment(weekStart, 'YYYY-MM-DD')
  const end = moment(weekEnd, 'YYYY-MM-DD')
  if (!start.isValid() || !end.isValid()) return `${weekStart} – ${weekEnd}`
  return `Week of ${start.format('ddd MMM D')} – ${end.format('ddd MMM D')}`
}

function utilizationStatus(util) {
  const isOver = util != null && util > 100
  const isNear = util != null && util > 90 && util <= 100
  return {
    isOver,
    isNear,
    fillColor: isOver ? '#D0342C' : isNear ? '#D98E00' : '#4241BF',
    badgeBg: isOver ? '#FBE7E5' : isNear ? '#FBF1DE' : '#E6F5EE',
    badgeFg: isOver ? '#D0342C' : isNear ? '#D98E00' : '#00A162',
    badgeLabel: isOver ? 'Over' : isNear ? 'Near cap' : 'On track',
  }
}

function StaffUtilizationTable({
  rows,
  iso,
  highlightPerson = null,
  loading = false,
  error = null,
}) {
  const [expanded, setExpanded] = useState(() => new Set())

  const toggleExpanded = useCallback(personId => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(personId)) next.delete(personId)
      else next.add(personId)
      return next
    })
  }, [])

  const colCount = 8

  return (
    <Section id="utilization" data-testid="staff-utilization-section">
      <Card>
        <TblHead>
          <HeadT>Staff Utilization</HeadT>
          <HeadSub>Hours logged vs. weekly assignment × month</HeadSub>
        </TblHead>
        {loading ? (
          <LoadingWrap>
            <CircularProgress size={28} />
          </LoadingWrap>
        ) : (
          <Table>
            <thead>
              <tr>
                <th style={{ width: 40 }} aria-label="Expand" />
                <th>Person</th>
                <th className="num">Hours</th>
                <th className="num">Assignment</th>
                <th style={{ width: 180 }}>Utilization</th>
                <th className="num">Δ</th>
                <th className="num">Fees</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={colCount} style={{ color: '#D0342C', fontSize: 13 }}>
                    {error}
                  </td>
                </tr>
              ) : null}
              {!error && rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} style={{ color: '#676767', fontSize: 13 }}>
                    No billable time entries (RAW/BILL) for this month.
                  </td>
                </tr>
              ) : null}
              {!error &&
                rows.map((p, i) => {
                  const util = p.utilizationPct
                  const {
                    isOver,
                    isNear,
                    fillColor,
                    badgeBg,
                    badgeFg,
                    badgeLabel,
                  } = utilizationStatus(util)
                  const fillPct = util != null ? Math.min(util, 140) : 0
                  const highlight = highlightPerson === p.personName
                  const deltaStr =
                    util == null
                      ? '—'
                      : util > 100
                      ? `+${Math.round(util - 100)}%`
                      : `-${Math.round(100 - util)}%`
                  const isOpen = expanded.has(p.personId)
                  const weeks = p.weeklyBreakdown || []

                  return (
                    <React.Fragment key={p.personId}>
                      <tr
                        data-testid={`staff-util-row-${p.personId}`}
                        style={highlight ? { background: '#EEEEFF' } : {}}
                      >
                        <td>
                          <IconButton
                            size="small"
                            aria-label={
                              isOpen ? 'Collapse week breakdown' : 'Expand week breakdown'
                            }
                            aria-expanded={isOpen}
                            onClick={() => toggleExpanded(p.personId)}
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        </td>
                        <td>
                          <Person>
                            <Avatar $bg={avColors[i % avColors.length]}>
                              {String(p.personName || '')
                                .split(' ')
                                .filter(Boolean)
                                .map(s => s[0])
                                .join('')}
                            </Avatar>
                            <div>
                              <div style={{ fontWeight: 500 }}>{p.personName}</div>
                              <div style={{ fontSize: 11, color: '#676767' }}>
                                {p.entriesCount} entries
                              </div>
                            </div>
                          </Person>
                        </td>
                        <td className="num">{Number(p.hoursLogged).toFixed(1)}</td>
                        <td className="num" style={{ color: '#676767' }}>
                          {p.weeklyAssignedHours != null &&
                          Number.isFinite(Number(p.weeklyAssignedHours))
                            ? `${Number(p.weeklyAssignedHours)}h/wk`
                            : '—'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <UtilBar>
                              {util != null ? (
                                <Fill $w={`${(fillPct / 140) * 100}%`} $c={fillColor} />
                              ) : null}
                            </UtilBar>
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                minWidth: 42,
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                                color: isOver ? '#D0342C' : isNear ? '#D98E00' : '#050519',
                              }}
                            >
                              {util != null ? `${Math.round(util)}%` : '—'}
                            </span>
                          </div>
                        </td>
                        <td
                          className="num"
                          style={{ color: isOver ? '#D0342C' : isNear ? '#D98E00' : '#676767' }}
                        >
                          {deltaStr}
                        </td>
                        <td className="num">{formatCurrency(p.fees, iso)}</td>
                        <td>
                          <Badge $bg={badgeBg} $fg={badgeFg}>
                            {badgeLabel}
                          </Badge>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr data-testid={`staff-util-weeks-${p.personId}`}>
                          <td
                            colSpan={colCount}
                            style={{ background: '#fafafa', padding: '8px 18px' }}
                          >
                            <WeekGrid>
                              <thead>
                                <tr>
                                  <th>Week</th>
                                  <th className="num">Logged</th>
                                  <th className="num">Assigned</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {weeks.map(w => {
                                  const missing = w.hoursLogged === 0 && w.assignedHours > 0
                                  return (
                                    <tr key={w.weekStart}>
                                      <td>{formatWeekRange(w.weekStart, w.weekEnd)}</td>
                                      <td className="num">{Number(w.hoursLogged).toFixed(1)}</td>
                                      <td className="num">{Number(w.assignedHours).toFixed(1)}</td>
                                      <td>{missing ? <MissingChip>Missing</MissingChip> : null}</td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </WeekGrid>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  )
                })}
            </tbody>
          </Table>
        )}
      </Card>
    </Section>
  )
}

StaffUtilizationTable.propTypes = {
  rows: PropTypes.arrayOf(rowShape).isRequired,
  iso: PropTypes.string.isRequired,
  highlightPerson: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
}

export default StaffUtilizationTable
